import { AD_SLOTS } from "../../config/adSlots";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  subscribeMessagesWithReplies,
  postMessage,
  postReply,
  toggleStar,
  getTopicTitle,
  editMessage,
  deleteMessage,
} from "../../services/forumService";
import { compressImageToDataURL } from "../../utils/imageUtils";
import { auth } from "../../services/firebaseService";
import {
  Edit3,
  Trash2,
  Share2,
  Star,
  Star as StarFilled,
  Reply,
  X as CloseIcon,
} from "lucide-react";

import ConfirmDialog from "../common/ConfirmDialogProps";
import AdBanner from "../ads/AdBanner";

const InfoDialog: React.FC<{
  message: string;
  isOpen: boolean;
  onClose: () => void;
}> = ({ message, isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-sm text-white text-center">
        <p className="text-sm mb-4">{message}</p>
        <button
          onClick={onClose}
          className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
        >
          OK
        </button>
      </div>
    </div>
  );
};

const ReplyDialog: React.FC<{
  isOpen: boolean;
  onSend: (text: string) => void;
  onCancel: () => void;
}> = ({ isOpen, onSend, onCancel }) => {
  const [text, setText] = useState("");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-gray-800 p-6 rounded-lg w-full max-w-sm space-y-4">
        <h3 className="text-lg text-white">Reply to Message</h3>
        <textarea
          rows={3}
          className="w-full bg-gray-700 text-white px-3 py-2 rounded"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type your reply..."
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="bg-gray-600 text-white px-4 py-2 rounded"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onSend(text);
              setText("");
            }}
            className="bg-green-600 text-white px-4 py-2 rounded"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default function MessageThread() {
  const { forumId, topicId } = useParams<{
    forumId: string;
    topicId: string;
  }>();
  const navigate = useNavigate();
  const user = auth.currentUser;

  const [topicTitle, setTopicTitle] = useState<string>("");
  const [msgs, setMsgs] = useState<any[]>([]);
  const [newText, setNewText] = useState("");
  const [attached, setAttached] = useState<File | null>(null);
  const [editingFor, setEditingFor] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [infoMsg, setInfoMsg] = useState("");
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!forumId || !topicId) return;
    getTopicTitle(forumId, topicId).then((title) => {
      if (title) setTopicTitle(title);
    });
  }, [forumId, topicId]);

  useEffect(() => {
    if (!forumId || !topicId) return;
    return subscribeMessagesWithReplies(forumId, topicId, setMsgs);
  }, [forumId, topicId]);

  useEffect(() => {
    if (!user) navigate("/forums");
  }, [user, navigate]);

  if (!user) return null;

  const handleAttach = () => fileInput.current?.click();
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setAttached(e.target.files[0]);
  };

  const onPost = async () => {
    if (!forumId || !topicId || !newText.trim()) return;
    await postMessage(forumId, topicId, newText.trim(), user.uid);
    if (attached) {
      const dataUrl = await compressImageToDataURL(attached, 800, 800, 0.7);
      const imgHtml = `<img src="${dataUrl}" alt="attachment" class="rounded border"/>`;
      await postMessage(forumId, topicId, imgHtml, user.uid);
      setAttached(null);
    }
    setNewText("");
  };

  const saveEdit = async (msgId: string) => {
    await editMessage(forumId!, topicId!, msgId, editText.trim());
    setEditingFor(null);
  };

  const startEdit = (m: any) => {
    setEditingFor(m.id);
    setEditText(m.text || "");
  };

  const onRequestDelete = (msgId: string) => {
    setPendingDeleteId(msgId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (pendingDeleteId) {
      await deleteMessage(forumId!, topicId!, pendingDeleteId);
      setPendingDeleteId(null);
      setShowDeleteDialog(false);
    }
  };

  const onShare = (msgId: string) => {
    const url = `${window.location.origin}${window.location.hash}#msg-${msgId}`;
    navigator.clipboard.writeText(url);
    setInfoMsg("Link copied to clipboard!");
    setShowInfoDialog(true);
  };

  const handleReplySend = async (text: string) => {
    if (replyTo && text.trim()) {
      await postReply(forumId!, topicId!, replyTo, text.trim(), user.uid);
      setReplyTo(null);
    }
  };

  const renderMessage = (m: any, level = 0) => {
    const isStarred = m.stars?.includes(user.uid);
    const starCount = m.stars?.length || 0;
    return (
      <div
        key={m.id}
        className={`ml-${level * 4} mb-4 bg-gray-700 p-3 rounded`}
        id={`msg-${m.id}`}
      >
        <div className="flex justify-between items-center mb-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-blue-400">
            {m.avatarUrl ? (
              <img
                src={m.avatarUrl}
                className="w-6 h-6 rounded-full"
                alt={`${m.authorName}'s avatar`}
                title={`${m.authorName}'s avatar`}
              />
            ) : (
              <div className="w-6 h-6 rounded-full bg-gray-500 text-white flex items-center justify-center">
                {m.authorName?.[0] || "?"}
              </div>
            )}
            @{m.authorName}
          </div>
          <span className="text-xs text-gray-400">
            {m.timestamp?.seconds
              ? new Date(m.timestamp.seconds * 1000).toLocaleString()
              : ""}
          </span>
        </div>
        {editingFor === m.id ? (
          <>
            <textarea
              className="w-full p-2 bg-gray-600 text-white rounded"
              rows={2}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => saveEdit(m.id)}
                className="bg-blue-600 px-3 py-1 text-white rounded"
              >
                Save
              </button>
              <button
                onClick={() => setEditingFor(null)}
                className="bg-gray-500 px-3 py-1 text-white rounded"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div
            className="prose prose-invert"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.text) }}
          />
        )}

        <div className="mt-2 flex gap-3 items-center">
          <button
            onClick={() => toggleStar(forumId!, topicId!, m.id, user.uid)}
            className="flex items-center gap-1"
          >
            <Star size={16} fill={isStarred ? "white" : "none"} />
            <span className="text-xs text-white">
              {starCount > 0 ? starCount : ""}
            </span>
          </button>
          <button onClick={() => setReplyTo(m.id)}>
            <Reply size={16} />
          </button>
          {m.authorId === user.uid && (
            <>
              <button onClick={() => startEdit(m)}>
                <Edit3 size={16} />
              </button>
              <button onClick={() => onRequestDelete(m.id)}>
                <Trash2 size={16} />
              </button>
            </>
          )}
          <button onClick={() => onShare(m.id)}>
            <Share2 size={16} />
          </button>
        </div>

        {m.replies?.length > 0 && (
          <div className="mt-2 ml-4 border-l border-gray-600 pl-4">
            {m.replies.map((reply: any) => renderMessage(reply, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="sticky top-0 z-10 bg-gray-800 border-b border-gray-700 p-4">
        <Link to="/forums" className="text-blue-400 hover:underline block mb-1">
          ← Back to Forums
        </Link>
        <div>
          <h4 className="text-sm font-bold text-white">{topicTitle}</h4>
          <h6 className="text-xs font-bold text-white">
            {msgs.length} comments
          </h6>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {msgs.map((m) => renderMessage(m))}
      </div>

      <div className="my-6">
        <AdBanner
          adSlot={AD_SLOTS.forumThread}
          adFormat="horizontal"
          fullWidthResponsive={true}
          style={{ maxWidth: '728px', margin: '0 auto' }}
        />
      </div>

      <div className="bg-gray-800 p-4 rounded-t space-y-2">
        <textarea
          rows={2}
          className="w-full p-2 bg-gray-700 text-white rounded"
          placeholder="Type your message..."
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
        />
        <div className="flex items-center space-x-4">
          <button
            onClick={handleAttach}
            className="px-3 py-1 bg-blue-600 rounded text-white"
          >
            Attach Image
          </button>
          {attached && (
            <span className="text-sm text-gray-300">
              {attached.name}{" "}
              <button
                onClick={() => setAttached(null)}
                className="underline text-red-400"
              >
                Remove
              </button>
            </span>
          )}
          <button
            onClick={onPost}
            className="ml-auto px-4 py-2 bg-green-600 rounded text-white"
          >
            Post
          </button>
        </div>
        <input
          type="file"
          accept="image/*"
          ref={fileInput}
          className="hidden"
          onChange={onFileChange}
        />
      </div>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Message?"
        message="This action is irreversible. Are you sure you want to delete this message?"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteDialog(false)}
      />

      <InfoDialog
        isOpen={showInfoDialog}
        message={infoMsg}
        onClose={() => setShowInfoDialog(false)}
      />

      <ReplyDialog
        isOpen={!!replyTo}
        onSend={handleReplySend}
        onCancel={() => setReplyTo(null)}
      />
    </div>
  );
}
